import * as ents from '@attain-apps/entities'

import type { Edit } from './types'

/**
 * The current selection of every dimension-filter plugin, keyed by the
 * configuration filter field it fills in.
 *
 * NOTE: the plugins are collection-wide rather than per report, so a selection
 * carries over when the open report changes — each report just takes the
 * filters its own configuration declares
 */
export interface DimensionFilters {
  readonly accountFilter?: ents.extra.space.Record.Filter.DimensionFilter
  readonly clusterFilter?: ents.extra.space.Record.Filter.DimensionFilter
  readonly employeeFilter?: ents.extra.space.Record.Filter.DimensionFilter
  readonly organizationFilter?: ents.extra.space.Record.Filter.DimensionFilter
  readonly roomTypeFilter?: ents.extra.space.Record.Filter.DimensionFilter
  readonly spaceFilter?: ents.extra.space.Record.Filter.DimensionFilter
}

/** Everything the configuration plugin needs to know about one report. */
interface Handler<TId extends ents.extra.space.Report.Id> {
  /**
   * Folds an edit into the report's stored settings. Reports with a `filter`
   * merge it one level deep, so an edit can change a single filter field
   */
  readonly applyEdit: (
    settings: ents.extra.space.Report.Configuration<TId>,
    edit: Edit<TId>,
  ) => ents.extra.space.Report.Configuration<TId>

  /**
   * Folds the current dimension-filter selections into the report's
   * configuration, taking only the dimension filters its `filter` declares.
   *
   * NOTE: left out by reports that have no dimension filters, rather than
   * declared as a no-op
   */
  readonly composeDimensionFilters?: (
    settings: ents.extra.space.Report.Configuration<TId>,
    dimensionFilters: DimensionFilters,
  ) => ents.extra.space.Report.Configuration<TId>
}

/**
 * Per-report handling, keyed by report ID.
 *
 * NOTE: adding a report to {@link ents.extra.space.Report.Id} is a compile
 * error until it is listed here, which is what keeps every report's
 * configuration handled explicitly rather than assumed to look like another's
 */
const HANDLERS: {
  readonly [TId in ents.extra.space.Report.Id]: Handler<TId>
} = {
  [ents.extra.space.Report.Id.AccountList]: {
    applyEdit: (settings, edit) => ({
      ...settings,
      ...edit,
      filter: { ...settings.filter, ...edit.filter },
    }),
    // NOTE: only the organization filter — the one this report's `filter`
    // declares
    composeDimensionFilters: (settings, { organizationFilter }) => ({
      ...settings,
      filter: { ...settings.filter, organizationFilter },
    }),
  },
  [ents.extra.space.Report.Id.DataExport]: {
    applyEdit: (settings, edit) => ({
      ...settings,
      ...edit,
      filter: { ...settings.filter, ...edit.filter },
    }),
    // NOTE: only the filters this report's `filter` declares — the plugins are
    // shared by every report, so a selection made for another report (e.g. Room
    // Types) must not carry over
    composeDimensionFilters: (
      settings,
      { clusterFilter, organizationFilter, spaceFilter },
    ) => ({
      ...settings,
      filter: {
        ...settings.filter,
        clusterFilter,
        organizationFilter,
        spaceFilter,
      },
    }),
  },
  [ents.extra.space.Report.Id.EmployeeList]: {
    applyEdit: (settings, edit) => ({
      ...settings,
      ...edit,
      filter: { ...settings.filter, ...edit.filter },
    }),
    // NOTE: only the organization filter — the one this report's `filter`
    // declares
    composeDimensionFilters: (settings, { organizationFilter }) => ({
      ...settings,
      filter: { ...settings.filter, organizationFilter },
    }),
  },
  [ents.extra.space.Report.Id.RoomLinks]: {
    applyEdit: (settings, edit) => ({
      ...settings,
      ...edit,
    }),
  },
  [ents.extra.space.Report.Id.RoomSupportingInformation]: {
    applyEdit: (settings, edit) => ({
      ...settings,
      ...edit,
      filter: { ...settings.filter, ...edit.filter },
    }),
    // NOTE: only the filters this report's `filter` declares — the plugins are
    // shared by every report, so a selection made for another report (e.g.
    // Accounts) must not carry over
    composeDimensionFilters: (
      settings,
      {
        clusterFilter,
        employeeFilter,
        organizationFilter,
        roomTypeFilter,
        spaceFilter,
      },
    ) => ({
      ...settings,
      filter: {
        ...settings.filter,
        clusterFilter,
        employeeFilter,
        organizationFilter,
        roomTypeFilter,
        spaceFilter,
      },
    }),
  },
  [ents.extra.space.Report.Id.RoomsByAccount]: {
    applyEdit: (settings, edit) => ({
      ...settings,
      ...edit,
      filter: { ...settings.filter, ...edit.filter },
    }),
    // NOTE: only the account filter — the one this report's `filter` declares
    composeDimensionFilters: (settings, { accountFilter }) => ({
      ...settings,
      filter: { ...settings.filter, accountFilter },
    }),
  },
  [ents.extra.space.Report.Id.RoomsByOccupant]: {
    applyEdit: (settings, edit) => ({
      ...settings,
      ...edit,
      filter: { ...settings.filter, ...edit.filter },
    }),
    // NOTE: only the employee filter — the one this report's `filter` declares
    composeDimensionFilters: (settings, { employeeFilter }) => ({
      ...settings,
      filter: { ...settings.filter, employeeFilter },
    }),
  },
  [ents.extra.space.Report.Id.RoomsByResponsiblePerson]: {
    applyEdit: (settings, edit) => ({
      ...settings,
      ...edit,
      filter: { ...settings.filter, ...edit.filter },
    }),
    // NOTE: only the employee filter — the one this report's `filter` declares
    composeDimensionFilters: (settings, { employeeFilter }) => ({
      ...settings,
      filter: { ...settings.filter, employeeFilter },
    }),
  },
  [ents.extra.space.Report.Id.SpaceSalaryComparison]: {
    applyEdit: (settings, edit) => ({
      ...settings,
      ...edit,
      filter: { ...settings.filter, ...edit.filter },
    }),
    // NOTE: only the organization filter — the one this report's `filter`
    // declares
    composeDimensionFilters: (settings, { organizationFilter }) => ({
      ...settings,
      filter: { ...settings.filter, organizationFilter },
    }),
  },
  [ents.extra.space.Report.Id.UnassignedSponsoredAccounts]: {
    applyEdit: (settings, edit) => ({
      ...settings,
      ...edit,
      filter: { ...settings.filter, ...edit.filter },
    }),
    // NOTE: only the organization filter — the one this report's `filter`
    // declares, and the one the table mirrors over when it is applied (see
    // `mirrorOrganizationFilterToReport`)
    composeDimensionFilters: (settings, { organizationFilter }) => ({
      ...settings,
      filter: { ...settings.filter, organizationFilter },
    }),
  },
  [ents.extra.space.Report.Id.UnassignedSponsoredPeople]: {
    applyEdit: (settings, edit) => ({
      ...settings,
      ...edit,
      filter: { ...settings.filter, ...edit.filter },
    }),
    // NOTE: only the organization filter — the one this report's `filter`
    // declares, and the one the table mirrors over when it is applied (see
    // `mirrorOrganizationFilterToReport`)
    composeDimensionFilters: (settings, { organizationFilter }) => ({
      ...settings,
      filter: { ...settings.filter, organizationFilter },
    }),
  },
  [ents.extra.space.Report.Id.ProgressByOrganization]: {
    applyEdit: (settings, edit) => ({
      ...settings,
      ...edit,
    }),
  },
  [ents.extra.space.Report.Id.UnclaimedRooms]: {
    applyEdit: (settings, edit) => ({
      ...settings,
      ...edit,
    }),
  },
}

/**
 * @see {@link Handler.applyEdit}
 *
 * NOTE: indexing {@link HANDLERS} with the report ID is what keeps `settings`
 * and `edit` correlated to it — see TypeScript's correlated union pattern
 */
export function applyEdit<TId extends ents.extra.space.Report.Id>(
  id: TId,
  settings: ents.extra.space.Report.Configuration<TId>,
  edit: Edit<TId>,
): ents.extra.space.Report.Configuration<TId> {
  return HANDLERS[id].applyEdit(settings, edit)
}

/**
 * @see {@link Handler.composeDimensionFilters}
 * @see {@link applyEdit} for how the report ID keeps this type-safe
 */
export function composeDimensionFilters<TId extends ents.extra.space.Report.Id>(
  id: TId,
  settings: ents.extra.space.Report.Configuration<TId>,
  dimensionFilters: DimensionFilters,
): ents.extra.space.Report.Configuration<TId> {
  return (
    HANDLERS[id].composeDimensionFilters?.(settings, dimensionFilters) ??
    settings
  )
}
